/* eslint no-underscore-dangle: 0 */
import _ from 'underscore';

export type TelemetryOptions = Record<string, unknown>;
export type AdditionalDetails = unknown;

export interface TelemetryError {
  name?: string;
  errors?: TelemetryError[];
  type?: string;
  propertyName?: string;
  errorCode?: string;
  [key: string]: unknown;
}

export interface TelemetryLog {
  startScenario: (scenarioName: string) => unknown;
  endScenario: (scenarioName: string, pass: boolean, returnObj: string) => unknown;
  trace?: (api: unknown, details: string, scenarioName: string) => unknown;
}

export interface TappablePromise<T = unknown> {
  tap: (handler: (value: T) => unknown) => TappablePromise<T>;
  tapCatch: (handler: (error: TelemetryError) => unknown) => TappablePromise<T>;
}

export interface ScenarioLogger {
  setOptions: (options: TelemetryOptions) => ScenarioLogger;
  setOption: (key: string, value: unknown) => ScenarioLogger;
  clearOptions: (ignoreKeys?: string[]) => ScenarioLogger;
  getAction: () => string | undefined;
  start: () => ScenarioLogger;
  end: (pass: boolean, additionalDetails?: AdditionalDetails) => ScenarioLogger;
  fail: (error?: TelemetryError | TelemetryError[], additionalDetails?: AdditionalDetails) => ScenarioLogger;
  succeed: (additionalDetails?: AdditionalDetails) => ScenarioLogger;
  trace: (api: unknown, additionalDetails?: AdditionalDetails) => ScenarioLogger;
  tap: <T>(promise: TappablePromise<T>) => TappablePromise<T>;
  tapCatch: <T>(promise: TappablePromise<T>) => TappablePromise<T>;
  addTaps: <T>(promise: TappablePromise<T>) => TappablePromise<T>;
  addTap: <T>(promise: TappablePromise<T>) => TappablePromise<T>;
  addTapCatch: <T>(promise: TappablePromise<T>) => TappablePromise<T>;
}

export interface SubmitScenarioLogger extends ScenarioLogger {
  validationFail: (error: TelemetryError, additionalDetails?: AdditionalDetails) => SubmitScenarioLogger;
}

export interface PidlLogger {
  _loggingOptions: TelemetryOptions;
  _pidlLoggingOptions: TelemetryOptions;
  _currentPidlCall?: string;
  overall: ScenarioLogger;
  overallRender: ScenarioLogger;
  overallSubmit: ScenarioLogger;
  renderPanel: ScenarioLogger;
  auth: ScenarioLogger;
  getPidl: ScenarioLogger;
  threatCreate: ScenarioLogger;
  addressFetch: ScenarioLogger;
  renderPidl: ScenarioLogger;
  submit: SubmitScenarioLogger;
  addOrUpdatePI: ScenarioLogger;
  getBaseScenarioName: () => string;
  getOption: (key: string) => unknown;
  getPidlOption: (key: string) => unknown;
  setOptions: (options: TelemetryOptions) => PidlLogger;
  setOption: (key: string, value: unknown) => PidlLogger;
  setPidlOptions: (options: TelemetryOptions) => PidlLogger;
  setPidlOption: (key: string, value: unknown) => PidlLogger;
  clearOptions: (ignoreFromLoggingOptionsKeys?: string[]) => PidlLogger;
  start: ScenarioLogger['start'];
  trace: ScenarioLogger['trace'];
  succeed: (additionalDetails?: AdditionalDetails) => void;
  fail: (reason?: unknown, source?: unknown, error?: TelemetryError, additionalDetails?: AdditionalDetails) => PidlLogger;
  userFail: (reason: unknown, additionalDetails?: AdditionalDetails) => void;
  pidlFail: (error: TelemetryError, additionalDetails?: AdditionalDetails) => PidlLogger;
}

export interface PidlTelemetry {
  createLogger: (context: unknown, workflow: unknown) => PidlLogger;
  create: PidlTelemetry['createLogger'];
}

type ScenarioLoggerConstructor = new (logger: PidlLogger, scenarioAction?: string) => ScenarioLogger;
type PidlLoggerConstructor = new (context: unknown, workflow: unknown) => PidlLogger;

export const initialize = (log: TelemetryLog): PidlTelemetry => {
  // General ScenarioLogger
  function ScenarioLogger(this: ScenarioLogger, logger: PidlLogger, scenarioAction?: string) {
    const scenarioLogger = this;
    const loggingOptions = logger._loggingOptions;
    const scenarioLoggingOptions: TelemetryOptions = {};
    let scenarioName = logger.getBaseScenarioName();

    if (scenarioAction) {
      scenarioName += `_${scenarioAction}`;
    }
    scenarioLogger.setOptions = (options: TelemetryOptions) => {
      _.extend(scenarioLoggingOptions, options);
      return scenarioLogger;
    };
    scenarioLogger.setOption = (key: string, value: unknown) => {
      scenarioLoggingOptions[key] = value;
      return scenarioLogger;
    };
    scenarioLogger.clearOptions = (ignoreKeys?: string[]) => {
      _.each(scenarioLoggingOptions, (value: unknown, key: string) => {
        if (!_.contains(ignoreKeys, key)) {
          delete scenarioLoggingOptions[key];
        }
      });
      return scenarioLogger;
    };
    scenarioLogger.getAction = () => scenarioAction;

    scenarioLogger.start = () => {
      log.startScenario(scenarioName);
      return scenarioLogger;
    };
    scenarioLogger.end = (pass: boolean, additionalDetails?: AdditionalDetails) => {
      const returnObj = _.extend({}, additionalDetails, loggingOptions, scenarioLoggingOptions);

      log.endScenario(scenarioName, pass, JSON.stringify(returnObj));
      scenarioLogger.clearOptions();

      return scenarioLogger;
    };
    scenarioLogger.fail = (error?: TelemetryError | TelemetryError[], additionalDetails?: AdditionalDetails) => {
      const errorData: { errors?: TelemetryError[]; error?: TelemetryError } = {};
      if (_.isArray(error)) {
        errorData.errors = error;
      } else if (error) {
        errorData.error = error;
      }
      return scenarioLogger.end(false, _.extend(errorData, additionalDetails));
    };
    scenarioLogger.succeed = (additionalDetails?: AdditionalDetails) => scenarioLogger.end(true, additionalDetails);
    scenarioLogger.trace = (Api: unknown, additionalDetails?: AdditionalDetails) => {
      log.trace?.(Api, JSON.stringify(_.extend({}, additionalDetails, loggingOptions, scenarioLoggingOptions)), scenarioName);
      return scenarioLogger;
    };
    scenarioLogger.tap = <T>(promise: TappablePromise<T>) => promise.tap(() => scenarioLogger.succeed());
    scenarioLogger.tapCatch = <T>(promise: TappablePromise<T>) => promise.tapCatch((error: TelemetryError) => scenarioLogger.fail(error));
    scenarioLogger.addTaps = <T>(promise: TappablePromise<T>) => {
      scenarioLogger.start();
      return scenarioLogger.tap(scenarioLogger.tapCatch(promise));
    };
    scenarioLogger.addTap = <T>(promise: TappablePromise<T>) => {
      scenarioLogger.start();
      return scenarioLogger.tap(promise);
    };
    scenarioLogger.addTapCatch = <T>(promise: TappablePromise<T>) => {
      scenarioLogger.start();
      return scenarioLogger.tapCatch(promise);
    };
    return scenarioLogger;
  }

  // Custom ScenarioLoggers
  function PidlScenarioLogger(this: ScenarioLogger, logger: PidlLogger, scenarioAction: string) {
    const pidlScenarioLogger = this;
    ScenarioLogger.call(pidlScenarioLogger, logger, scenarioAction);

    const parentStart = pidlScenarioLogger.start;
    const parentEnd = pidlScenarioLogger.end;

    pidlScenarioLogger.start = () => {
      // eslint-disable-next-line no-param-reassign
      logger._currentPidlCall = scenarioAction;
      parentStart();
      return pidlScenarioLogger;
    };
    pidlScenarioLogger.end = (pass: boolean, additionalDetails?: AdditionalDetails) => {
      parentEnd(pass, _.extend({}, additionalDetails, logger._pidlLoggingOptions));
      // eslint-disable-next-line no-param-reassign
      logger._currentPidlCall = undefined;
      return pidlScenarioLogger;
    };
  }
  PidlScenarioLogger.prototype = Object.create(ScenarioLogger.prototype);
  function OverallScenarioLogger(this: ScenarioLogger, logger: PidlLogger) {
    const overallScenarioLogger = this;

    ScenarioLogger.call(overallScenarioLogger, logger);

    const parentStart = overallScenarioLogger.start;
    const parentSucceed = overallScenarioLogger.succeed;

    overallScenarioLogger.start = () => {
      parentStart();
      logger.overallRender.start();
      return overallScenarioLogger;
    };
    overallScenarioLogger.succeed = (additionalDetails?: AdditionalDetails) => {
      parentSucceed(additionalDetails);
      logger.overallSubmit.succeed(additionalDetails);
      return overallScenarioLogger;
    };
  }
  OverallScenarioLogger.prototype = Object.create(ScenarioLogger.prototype);
  function RenderPidlScenarioLogger(this: ScenarioLogger, logger: PidlLogger) {
    const overallScenarioLogger = this;

    PidlScenarioLogger.call(overallScenarioLogger, logger, 'RenderingPidlDoc');

    const parentSucceed = overallScenarioLogger.succeed;
    overallScenarioLogger.succeed = (additionalDetails?: AdditionalDetails) => {
      parentSucceed(additionalDetails);
      logger.overallRender.succeed(additionalDetails);
      return overallScenarioLogger;
    };
  }
  RenderPidlScenarioLogger.prototype = Object.create(PidlScenarioLogger.prototype);
  function AuthScenarioLogger(this: ScenarioLogger, logger: PidlLogger) {
    const authScenarioLogger = this;
    const authPromiseScenarioName = 'AuthenticationPromise';
    ScenarioLogger.call(authScenarioLogger, logger, authPromiseScenarioName);

    const parentSucceed = authScenarioLogger.succeed;
    authScenarioLogger.tap = <T>(promise: TappablePromise<T>) => promise.tap((data) => {
      parentSucceed({ attempts: (data as { attempts?: unknown }).attempts });
    });
    authScenarioLogger.tapCatch = <T>(promise: TappablePromise<T>) => promise.tapCatch((error) => {
      const errReason = (error && error.name) || 'UnknownError';
      logger.fail(errReason, authPromiseScenarioName, error, { reason: errReason });
    });
  }
  AuthScenarioLogger.prototype = Object.create(ScenarioLogger.prototype);
  function SubmitScenarioLogger(this: SubmitScenarioLogger, logger: PidlLogger) {
    const submitScenarioLogger = this;
    const pidlSubmitScenarioName = 'PidlSubmit';
    PidlScenarioLogger.call(submitScenarioLogger, logger, pidlSubmitScenarioName);

    const parentStart = submitScenarioLogger.start;
    const parentFail = submitScenarioLogger.fail;

    submitScenarioLogger.start = () => {
      logger.overallSubmit.start();
      parentStart();
      return submitScenarioLogger;
    };
    submitScenarioLogger.validationFail = (error: TelemetryError, additionalDetails?: AdditionalDetails) => {
      const errors = error && error.errors;
      let logErrors: Array<TelemetryOptions | undefined> = [];
      let expiredCardLogged = false;
      let invalidAddressLogged = false;
      const parseError = (err: TelemetryError): TelemetryOptions | undefined => {
        const errorType = err.type;
        const { propertyName, errorCode } = err;

        if (errorType === 'userInputValidationError') {
          switch (errorCode) {
            case 'ExpiredCard':
              // only return once (this will return undefined
              // for the individual month & year properties, which will get filtered out,
              // so that only one error will be logged for an expired card)
              if (!expiredCardLogged) {
                expiredCardLogged = true;
                return {
                  errorType,
                  propertyName: 'expiryGroup',
                  errorCode,
                  handledError: true,
                };
              }
              break;
            case 'InvalidAddress':
              // only return once (this will return undefined for all
              // of the other address fields, which will get filtered out,
              // so that only one error will be logged for an invalid address)
              if (!invalidAddressLogged) {
                invalidAddressLogged = true;
                return {
                  errorType,
                  propertyName: 'addressGroup',
                  errorCode,
                  handledError: true,
                };
              }
              break;
            default:
              // log all other validation errors
              return {
                errorType,
                propertyName,
                errorCode,
                handledError: true,
              };
          }
        } else {
          // If any non-validation errors occur, we want all the info about them that we can get.
          // As we learn more about them, we can start logging them separately, or filter them out.
          return _.extend({ handledError: false }, err);
        }
        return undefined;
      };
      if (errors) {
        logErrors = _.map(errors, parseError);
      } else {
        logErrors.push(parseError(error));
      }

      parentFail(_.without(logErrors, undefined), additionalDetails);

      logger.overallSubmit.fail(error, {
        reason: 'ValidationErrors',
        source: pidlSubmitScenarioName,
      });

      return submitScenarioLogger;
    };
  }
  SubmitScenarioLogger.prototype = Object.create(PidlScenarioLogger.prototype);
  function AddOrUpdatePIScenarioLogger(this: ScenarioLogger, logger: PidlLogger) {
    const addOrUpdatePIScenarioLogger = this;
    const addOrUpdatePIScenarioName = 'Pidl_AddOrUpdatePI';
    ScenarioLogger.call(addOrUpdatePIScenarioLogger, logger, addOrUpdatePIScenarioName);
    addOrUpdatePIScenarioLogger.tapCatch = <T>(promise: TappablePromise<T>) => promise.tapCatch((error) => {
      logger.fail('PidlTerminatingError', addOrUpdatePIScenarioName, error);
    });
  }
  AddOrUpdatePIScenarioLogger.prototype = Object.create(ScenarioLogger.prototype);
  function PidlLogger(this: PidlLogger, context: unknown, workflow: unknown) {
    const pidlLogger = this;

    const loggingOptions: TelemetryOptions = {};
    const pidlLoggingOptions: TelemetryOptions = {};
    const baseScenarioName = `ModernPI_${context}_${workflow}`;
    pidlLogger.getBaseScenarioName = () => baseScenarioName;
    pidlLogger._loggingOptions = loggingOptions;
    pidlLogger._pidlLoggingOptions = pidlLoggingOptions;

    // Creating ScenarioLoggers
    pidlLogger.overall = new (OverallScenarioLogger as unknown as ScenarioLoggerConstructor)(pidlLogger);
    // overall render process starts when the overall scenario starts
    // ends when a terminating error stops pidl, or renderPidl succeeds
    pidlLogger.overallRender = new (ScenarioLogger as unknown as ScenarioLoggerConstructor)(pidlLogger, 'Pidl_OverallRender');
    // overall submit process starts when the submit scenario starts
    // ends when a terminating error stops pidl, pidlSubmit hits a validation error, or the overall scenario succeeds
    pidlLogger.overallSubmit = new (ScenarioLogger as unknown as ScenarioLoggerConstructor)(pidlLogger, 'Pidl_OverallSubmit');
    pidlLogger.renderPanel = new (ScenarioLogger as unknown as ScenarioLoggerConstructor)(pidlLogger, 'RenderPanel');
    pidlLogger.auth = new (AuthScenarioLogger as unknown as ScenarioLoggerConstructor)(pidlLogger);
    pidlLogger.getPidl = new (ScenarioLogger as unknown as ScenarioLoggerConstructor)(pidlLogger, 'PidlInfoPromise');
    pidlLogger.threatCreate = new (ScenarioLogger as unknown as ScenarioLoggerConstructor)(pidlLogger, 'ThreatModuleCreation');
    pidlLogger.addressFetch = new (ScenarioLogger as unknown as ScenarioLoggerConstructor)(pidlLogger, 'Pidl_AddressListFetch');
    pidlLogger.renderPidl = new (RenderPidlScenarioLogger as unknown as ScenarioLoggerConstructor)(pidlLogger);
    pidlLogger.submit = new (SubmitScenarioLogger as unknown as new (logger: PidlLogger) => SubmitScenarioLogger)(pidlLogger);
    pidlLogger.addOrUpdatePI = new (AddOrUpdatePIScenarioLogger as unknown as ScenarioLoggerConstructor)(pidlLogger);

    const scenarios = [
      pidlLogger.renderPanel,
      pidlLogger.auth,
      pidlLogger.getPidl,
      pidlLogger.threatCreate,
      pidlLogger.addressFetch,
      pidlLogger.renderPidl,
      pidlLogger.submit,
      pidlLogger.addOrUpdatePI,
    ];
    const overallScenarios = [
      pidlLogger.overallRender,
      pidlLogger.overallSubmit,
      pidlLogger.overall,
    ];
    pidlLogger.getOption = (key: string) => loggingOptions[key];
    pidlLogger.getPidlOption = (key: string) => pidlLoggingOptions[key];
    pidlLogger.setOptions = (options: TelemetryOptions) => {
      _.extend(loggingOptions, options);
      return pidlLogger;
    };
    pidlLogger.setOption = (key: string, value: unknown) => {
      loggingOptions[key] = value;
      return pidlLogger;
    };
    pidlLogger.setPidlOptions = (options: TelemetryOptions) => {
      _.extend(pidlLoggingOptions, options);
      return pidlLogger;
    };
    pidlLogger.setPidlOption = (key: string, value: unknown) => {
      pidlLoggingOptions[key] = value;
      return pidlLogger;
    };
    pidlLogger.clearOptions = (ignoreFromLoggingOptionsKeys?: string[]) => {
      _.each(loggingOptions, (val: unknown, key: string) => {
        if (!_.contains(ignoreFromLoggingOptionsKeys, key)) {
          delete loggingOptions[key];
        }
      });
      _.each(pidlLoggingOptions, (val: unknown, key: string) => {
        // don't delete correlation vector as it will stay constant until a new pidl instance is created.
        // which would create a new PidlLogger instance as well
        if (key !== 'correlationVector') {
          delete pidlLoggingOptions[key];
        }
      });
      return pidlLogger;
    };

    pidlLogger.start = pidlLogger.overall.start;
    pidlLogger.trace = pidlLogger.overall.trace;
    pidlLogger.succeed = (additionalDetails?: AdditionalDetails) => {
      // ending scenarios that haven't started has no effect, so this will end any active scenario
      _.each(scenarios, (scenario: ScenarioLogger) => { scenario.succeed(additionalDetails); });
      _.each(overallScenarios, (scenario: ScenarioLogger) => { scenario.succeed(additionalDetails); });
    };
    pidlLogger.fail = (reason?: unknown, source?: unknown, error?: TelemetryError, additionalDetails?: AdditionalDetails) => {
      const overallData = _.extend({
        reason,
        source,
      }, additionalDetails);

      // ending scenarios that haven't started has no effect,
      // so this will end any active scenario and have no effect on inactive scenarios
      _.each(scenarios, (scenario: ScenarioLogger) => { scenario.fail(error, additionalDetails); });

      // End overall scenarios with reason and source
      // ending scenarios that haven't started has no effect,
      // so this will end any active scenario and have no effect on inactive scenarios
      _.each(overallScenarios, (scenario: ScenarioLogger) => { scenario.fail(error, overallData); });

      return pidlLogger;
    };
    pidlLogger.userFail = (reason: unknown, additionalDetails?: AdditionalDetails) => {
      pidlLogger.fail(reason, 'User', undefined, additionalDetails);
    };
    pidlLogger.pidlFail = (error: TelemetryError, additionalDetails?: AdditionalDetails) => {
      const source = pidlLogger._currentPidlCall || 'Unknown Error';
      return pidlLogger.fail('PidlTerminatingError', source, error, additionalDetails);
    };

    return pidlLogger;
  }
  function createLogger(context: unknown, workflow: unknown) {
    return new (PidlLogger as unknown as PidlLoggerConstructor)(context, workflow);
  }

  // Exporting createLogger and create for backward compatibility
  return { createLogger, create: createLogger };
};
